import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import { InAppNotification, NotificationStats, PagedNotifications, NotificationFilters } from '../types';
import { useInAppNotificationStore } from '../stores/in-app-notification-store';

// --- API helpers ---

const fetchNotifications = (page: number, size: number, unreadOnly: boolean): Promise<PagedNotifications> =>
    AXIOS_INSTANCE.get('/api/notifications/in-app', { params: { page, size, unreadOnly } }).then(r => r.data);

const fetchUnreadCount = (): Promise<number> =>
    AXIOS_INSTANCE.get('/api/notifications/in-app/count').then(r => r.data);

const fetchStats = (): Promise<NotificationStats> =>
    AXIOS_INSTANCE.get('/api/notifications/in-app/stats').then(r => r.data);

const patchMarkAsRead = (id: number): Promise<InAppNotification> =>
    AXIOS_INSTANCE.patch(`/api/notifications/in-app/${id}/read`).then(r => r.data);

const patchMarkAllAsRead = (): Promise<number> =>
    AXIOS_INSTANCE.patch('/api/notifications/in-app/read-all').then(r => r.data);

// --- Query keys ---

export const IN_APP_NOTIFICATIONS_KEY = ['in-app-notifications'] as const;
export const IN_APP_STATS_KEY = ['in-app-notification-stats'] as const;

// --- Hook ---

export const useInAppNotifications = () => {
    const queryClient = useQueryClient();
    const {
        notifications, stats, filters, isLoading, error,
        currentPage, pageSize, totalElements, totalPages,
        setNotifications, setStats, setFilters, setLoading, setError,
        setCurrentPage, setPageSize, setPaginationInfo,
        markAsRead: markAsReadInStore, reset,
    } = useInAppNotificationStore();

    const notificationsQuery = useQuery({
        queryKey: [...IN_APP_NOTIFICATIONS_KEY, currentPage, pageSize, filters.unreadOnly],
        queryFn: () => fetchNotifications(currentPage, pageSize, filters.unreadOnly ?? false),
        staleTime: 30_000,
        refetchInterval: 60_000,
    });

    const statsQuery = useQuery({
        queryKey: IN_APP_STATS_KEY,
        queryFn: fetchStats,
        staleTime: 30_000,
        refetchInterval: 60_000,
    });

    const markAsReadMutation = useMutation({
        mutationFn: patchMarkAsRead,
        onSuccess: (_, id) => {
            markAsReadInStore(id);
            queryClient.invalidateQueries({ queryKey: IN_APP_STATS_KEY });
        },
        onError: () => message.error('Failed to mark notification as read'),
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: patchMarkAllAsRead,
        onSuccess: (count) => {
            queryClient.invalidateQueries({ queryKey: IN_APP_NOTIFICATIONS_KEY });
            queryClient.invalidateQueries({ queryKey: IN_APP_STATS_KEY });
            message.success(`${count} notification(s) marked as read`);
        },
        onError: () => message.error('Failed to mark all notifications as read'),
    });

    React.useEffect(() => {
        if (notificationsQuery.data) {
            const d = notificationsQuery.data;
            setNotifications(d.content ?? []);
            setPaginationInfo(d.totalElements ?? 0, d.totalPages ?? 0);
        }
    }, [notificationsQuery.data]);

    React.useEffect(() => {
        if (statsQuery.data) {
            setStats(statsQuery.data);
        }
    }, [statsQuery.data]);

    React.useEffect(() => {
        setLoading(notificationsQuery.isLoading || statsQuery.isLoading);
    }, [notificationsQuery.isLoading, statsQuery.isLoading]);

    React.useEffect(() => {
        const err = notificationsQuery.error || statsQuery.error;
        setError(err ? String(err) : null);
    }, [notificationsQuery.error, statsQuery.error]);

    return {
        notifications, stats, filters, isLoading, error,
        currentPage, pageSize, totalElements, totalPages,
        markAsRead: (id: number) => markAsReadMutation.mutateAsync(id),
        markAllAsRead: () => markAllAsReadMutation.mutateAsync(),
        isMarkingAllAsRead: markAllAsReadMutation.isPending,
        updateFilters: (f: NotificationFilters) => { setFilters(f); setCurrentPage(0); },
        goToPage: setCurrentPage,
        changePageSize: (s: number) => { setPageSize(s); setCurrentPage(0); },
        refreshNotifications: () => queryClient.invalidateQueries({ queryKey: IN_APP_NOTIFICATIONS_KEY }),
        reset,
    };
};
